import {
  collection,
  getDocs,
  getDoc,
  doc,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  documentId
} from 'firebase/firestore';
import { firestore } from './firebase/firebase';
import { MAILS_LIMIT, trierParDate } from '../utils/mailDelivery';

/**
 * Service « Clients » — fusion des données utilisateur + site web + création assistée.
 * Rappel du modèle : users/{uid}, sites/{uid} et users/{uid}/creation_request/creation_form
 * partagent le même identifiant (uid auth) → enrichissement 1:1 sans table de correspondance.
 */

const USERS_COLLECTION = 'users';
const SITES_COLLECTION = 'sites';
/** Journal des e-mails réellement envoyés (projet `msd-portail`, écrit par les fonctions). */
const MAIL_COLLECTION = 'mail';

// Taille maximale du jeu chargé en mémoire pour les recherches / filtres.
// Phase 1 : lecture directe (petits volumes). Si le parc grandit → onCall d'agrégation (Phase roadmap).
const CLIENTS_LIMIT = 500;

const toDateValue = (value) => {
  if (!value) return null;
  try {
    if (typeof value.toDate === 'function') return value.toDate();
    if (value.seconds) return new Date(value.seconds * 1000);
    if (value instanceof Date) return value;
    if (typeof value === 'string') return new Date(value);
    return null;
  } catch (error) {
    return null;
  }
};

/** Mappe un document utilisateur (identique à userService, + statut réel du doc) */
const mapUser = (id, data) => ({
  id,
  uid: data.uid || id,
  msdId: data.msdId || '',
  firstName: data.firstName || '',
  lastName: data.lastName || '',
  email: data.email || '',
  phone: data.phone || '',
  username: data.username || '',
  createdAt: toDateValue(data.createdAt),
  updatedAt: toDateValue(data.updatedAt),

  isCompany: data.isCompany || false,
  companyName: data.companyName || '',
  siretNumber: data.siretNumber || '',
  vatNumber: data.vatNumber || '',
  frLegalType: data.frLegalType || '',

  address1: data.address1 || '',
  city: data.city || '',
  postalCode: data.postalCode || '',
  state: data.state || '',
  country: data.country || '',

  birthDate: data.birthDate || '',
  birthCity: data.birthCity || '',
  birthPostalCode: data.birthPostalCode || '',
  birthCountry: data.birthCountry || '',

  subscriptionPlan: data.subscriptionPlan || '',
  subscriptionStatus: data.subscriptionStatus || '',
  subscriptionExpiryDate: toDateValue(data.subscriptionExpiryDate),
  subscriptionId: data.subscriptionId || '',
  subscriptionUpdatedAt: toDateValue(data.subscriptionUpdatedAt),
  stripeCustomerId: data.stripeCustomerId || '',

  welcomeEmailSent: data.welcomeEmailSent || false,
  welcomeEmailTimestamp: toDateValue(data.welcomeEmailTimestamp),

  status: data.status || (data.subscriptionStatus === 'active' ? 'active' : 'disabled'),
  role: data.isCompany ? 'business' : 'personal'
});

/** Lit le résumé « site » d'un utilisateur : sites/{uid} */
const readSiteSummary = async (uid) => {
  try {
    const snap = await getDoc(doc(firestore, SITES_COLLECTION, uid));
    if (!snap.exists()) return null;
    const data = snap.data();
    const domainName = data.publishInfo?.domainName || '';
    return {
      hasSite: true,
      domainName,
      publishedAt: toDateValue(data.publishInfo?.publishedAt),
      updatedAt: toDateValue(data.updatedAt || data.publishInfo?.updatedAt),
      publishId: data.publishInfo?.publishId || '',
      status: domainName ? 'published' : 'draft'
    };
  } catch (error) {
    console.warn(`clientService: résumé site introuvable pour ${uid}`, error?.message);
    return null;
  }
};

/** Lit le résumé « création assistée » : users/{uid}/creation_request/creation_form */
const readRequestSummary = async (uid) => {
  try {
    const snap = await getDoc(doc(firestore, USERS_COLLECTION, uid, 'creation_request', 'creation_form'));
    if (!snap.exists()) return null;
    const data = snap.data();
    return {
      hasRequest: true,
      status: data.status || '',
      trackingClosed: !!data.trackingClosed,
      feedbackRating: data.feedbackRating || null,
      submittedAt: toDateValue(data.submittedAt)
    };
  } catch (error) {
    console.warn(`clientService: demande introuvable pour ${uid}`, error?.message);
    return null;
  }
};

/** Enrichit un utilisateur avec son site + sa demande (lectures en parallèle) */
const enrichUser = async (docSnap) => {
  const user = mapUser(docSnap.id, docSnap.data());
  const uid = user.uid || user.id;
  const [site, request] = await Promise.all([readSiteSummary(uid), readRequestSummary(uid)]);
  return { ...user, site, request };
};

/** Filtre une ligne « client » selon le filtre actif */
const matchesFilter = (client, filter) => {
  switch (filter) {
    case 'all':
      return true;
    case 'site_en_ligne':
      return !!(client.site && client.site.domainName);
    case 'non_publie':
      return !(client.site && client.site.domainName);
    case 'creation_assistee':
      return !!(client.request && client.request.hasRequest && !client.request.trackingClosed);
    case 'entreprise':
      return !!client.isCompany;
    case 'abonnement_actif':
      return client.subscriptionStatus === 'active';
    default:
      return true;
  }
};

/** Recherche en mémoire (email, nom, entreprise, domaine) */
const matchesSearch = (client, search) => {
  if (!search) return true;
  const siteDomain = client.site?.domainName || '';
  const haystack = [
    client.email,
    client.firstName,
    client.lastName,
    client.companyName,
    client.username,
    siteDomain
  ].filter(Boolean).join(' ').toLowerCase();
  return haystack.includes(search);
};

/**
 * Récupère une page de clients enrichis (utilisateur + site + création assistée).
 * - Mode « liste » (aucun filtre / recherche) : pagination curseur Firestore.
 * - Mode « recherche / filtre » : jeu élargi limité (CLIENTS_LIMIT), filtrage en mémoire,
 *   puis découpe de la page — total = taille du jeu filtré (totalPages calculé côté UI).
 */
export const getClients = async (options = {}) => {
  const {
    pageSize = 10,
    page = 1,
    lastVisible = null,
    orderByField = 'createdAt',
    searchTerm = '',
    filter = 'all'
  } = options;

  const search = String(searchTerm || '').trim().toLowerCase();
  const isDefaultMode = !search && filter === 'all';

  let snapshotDocs = [];
  let newLastVisible = null;

  if (isDefaultMode) {
    // --- Mode curseur (liste « Tous » sans recherche) ---
    const baseQuery = lastVisible
      ? query(
          collection(firestore, USERS_COLLECTION),
          orderBy(orderByField),
          startAfter(lastVisible),
          limit(pageSize)
        )
      : query(
          collection(firestore, USERS_COLLECTION),
          orderBy(orderByField),
          limit(pageSize)
        );
    const snapshot = await getDocs(baseQuery);
    snapshotDocs = snapshot.docs;
    newLastVisible = snapshotDocs.length > 0
      ? snapshotDocs[snapshotDocs.length - 1]
      : null;
  } else {
    // --- Mode jeu élargi (recherche / filtres) ---
    // La recherche (nom, entreprise, domaine…) est faite en mémoire (matchesSearch)
    // sur le jeu chargé ; les filtres entreprise/abonnement actif pré-filtrent côté serveur.
    let usersQuery;
    if (filter === 'entreprise') {
      usersQuery = query(
        collection(firestore, USERS_COLLECTION),
        where('isCompany', '==', true),
        limit(CLIENTS_LIMIT)
      );
    } else if (filter === 'abonnement_actif') {
      usersQuery = query(
        collection(firestore, USERS_COLLECTION),
        where('subscriptionStatus', '==', 'active'),
        limit(CLIENTS_LIMIT)
      );
    } else {
      usersQuery = query(
        collection(firestore, USERS_COLLECTION),
        orderBy(orderByField),
        limit(CLIENTS_LIMIT)
      );
    }
    const snapshot = await getDocs(usersQuery);
    snapshotDocs = snapshot.docs;
  }

  // Enrichissement (site + demande) de chaque utilisateur de la page/du jeu
  let clients = await Promise.all(snapshotDocs.map((docSnap) => enrichUser(docSnap)));

  if (!isDefaultMode) {
    clients = clients
      .filter((client) => matchesFilter(client, filter))
      .filter((client) => matchesSearch(client, search));

    const startIndex = (page - 1) * pageSize;
    const sliced = clients.slice(startIndex, startIndex + pageSize);
    return {
      clients: sliced,
      lastVisible: null,
      isDataset: true,
      total: clients.length
    };
  }

  return {
    clients,
    lastVisible: newLastVisible,
    isDataset: false,
    total: null
  };
};

/**
 * Fiche client : charge le client enrichi (user + site + résumé demande).
 * @param {string} uid - Identifiant du compte (doc users/{uid})
 */
export const getClientDetail = async (uid) => {
  try {
    const userSnap = await getDoc(doc(firestore, USERS_COLLECTION, uid));
    if (!userSnap.exists()) return null;
    const client = mapUser(uid, userSnap.data());
    const [site, request] = await Promise.all([
      readSiteSummary(uid),
      readRequestSummary(uid)
    ]);
    return { client: { ...client, site, request } };
  } catch (error) {
    console.warn(`clientService: fiche client introuvable pour ${uid}`, error?.message);
    return null;
  }
};

/**
 * Détail complet de la demande de création assistée (messages, previews…).
 * @param {string} uid - Identifiant du compte
 */
export const getClientRequestDetail = async (uid) => {
  try {
    const snap = await getDoc(doc(firestore, USERS_COLLECTION, uid, 'creation_request', 'creation_form'));
    if (!snap.exists()) return null;
    const data = snap.data();
    return {
      userId: uid,
      ...data,
      createdAt: toDateValue(data.createdAt),
      updatedAt: toDateValue(data.updatedAt),
      submittedAt: toDateValue(data.submittedAt),
      feedbackSubmittedAt: toDateValue(data.feedbackSubmittedAt),
      previews: Array.isArray(data.previews) ? data.previews : [],
      messages: Array.isArray(data.messages) ? data.messages : []
    };
  } catch (error) {
    console.warn(`clientService: demande introuvable pour ${uid}`, error?.message);
    return null;
  }
};

/**
 * Tickets de support d'un client (les plus récents d'abord).
 * @param {string} uid - Identifiant du compte
 * @param {number} max - Nombre de tickets maximum
 */
export const getClientTickets = async (uid, max = 8) => {
  try {
    const q = query(
      collection(firestore, USERS_COLLECTION, uid, 'support_tickets'),
      orderBy('updatedAt', 'desc'),
      limit(max)
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map((docSnap) => {
      const data = docSnap.data();
      return {
        id: docSnap.id,
        title: data.title || data.subject || 'Ticket de support',
        status: data.status || 'new',
        priority: data.priority || 'medium',
        category: data.category || '',
        createdAt: toDateValue(data.createdAt),
        updatedAt: toDateValue(data.updatedAt)
      };
    });
  } catch (error) {
    console.warn(`clientService: tickets introuvables pour ${uid}`, error?.message);
    return [];
  }
};

/**
 * Mappe un document `mail` en objet d'écran.
 *
 * Forme réelle du document (`functions/services/email-utils.js` → `writeMailDocument`) :
 *   { to, message: {subject, html}, meta: {templateName, category, userId, timestamp,
 *     environment, [sentByUid], [forced]}, delivery: {...} }
 * `to` est à la RACINE (jamais dans `message`) et `message.text` n'existe pas : deux constats de la
 * mesure live C12 qui évitent d'afficher des champs vides par erreur de schéma.
 *
 * `delivery` est écrit par l'extension « Trigger Email » (100 % des documents mesurés) ; il peut
 * manquer sur un document très récent (extension pas encore passée) → replié sur `null`, ce qui
 * s'affiche « en attente d'envoi » et JAMAIS « échec ».
 * @param {string} id - Identifiant du document `mail`
 * @param {Object} data - Données brutes du document
 * @returns {Object} Envoi prêt à afficher
 */
const mapMail = (id, data = {}) => {
  const message = data.message || {};
  const meta = data.meta || {};
  const delivery = data.delivery || null;
  const info = (delivery && delivery.info) || {};

  return {
    id,
    to: data.to || '',
    message: {
      subject: message.subject || '',
      html: typeof message.html === 'string' ? message.html : ''
    },
    meta: {
      templateName: meta.templateName || '',
      category: meta.category || '',
      userId: meta.userId || '',
      environment: meta.environment || '',
      sentByUid: meta.sentByUid || '',
      forced: meta.forced === true,
      timestamp: toDateValue(meta.timestamp)
    },
    delivery: delivery ? {
      state: delivery.state || '',
      error: delivery.error || '',
      attempts: typeof delivery.attempts === 'number' ? delivery.attempts : null,
      messageId: info.messageId || '',
      accepted: Array.isArray(info.accepted) ? info.accepted : [],
      rejected: Array.isArray(info.rejected) ? info.rejected : [],
      startTime: toDateValue(delivery.startTime),
      endTime: toDateValue(delivery.endTime)
    } : null,
    createdAt: toDateValue(data.createdAt)
  };
};

/**
 * Envois d'e-mails d'un client — LECTURE SEULE.
 *
 * Le lien est `meta.userId` (uid du client), jamais l'adresse e-mail (DI2a-3 : l'adresse peut
 * changer, et certaines adresses de test sont partagées). Conséquence assumée : les documents
 * `mail` SANS `meta.userId` n'apparaissent pas — 62 sur 326 mesurés le 09/10/2026, et l'onglet le
 * dit à l'écran plutôt que de laisser croire à un journal complet.
 *
 * Aucun `orderBy` Firestore : cela exigerait un index composite (`mail (meta.userId ↑,
 * meta.timestamp ↓)`) donc un déploiement d'infrastructure pour un écran de consultation. Le tri
 * est fait EN MÉMOIRE par le module pur (`utils/mailDelivery`, DI2a-4). La borne de lecture
 * `MAILS_LIMIT` (200) suffit largement : le client le plus chargé compte 36 envois (mesure C12).
 * @param {string} uid - Identifiant du compte (doc `users/{uid}`)
 * @param {number} [max] - Nombre maximum d'envois lus
 * @returns {Promise<Array<Object>>} Envois triés du plus récent au plus ancien
 */
export const getClientEmails = async (uid, max = MAILS_LIMIT) => {
  if (!uid) return [];
  try {
    const q = query(
      collection(firestore, MAIL_COLLECTION),
      where('meta.userId', '==', uid),
      limit(max)
    );
    const snapshot = await getDocs(q);
    return trierParDate(snapshot.docs.map((docSnap) => mapMail(docSnap.id, docSnap.data())));
  } catch (error) {
    // L'erreur REMONTE : un écran de preuve ne doit pas afficher « aucun envoi » quand la lecture a
    // échoué (permission, réseau) — l'onglet distingue les deux situations.
    console.warn(`clientService: envois introuvables pour ${uid}`, error?.message);
    throw error;
  }
};

// --- Lot I5 : lectures TRANSVERSES du journal `mail` (vue « Emails en échec » du panneau) ---

/**
 * Résout le nom d'affichage de quelques clients (uid → nom), en LECTURE SEULE.
 *
 * La vue transverse « Emails en échec » (lot I5) doit NOMMER le client, pas seulement donner son
 * uid : un écran de support qui n'affiche qu'un uid ne sert à rien. Le nom vient de `users/{uid}`.
 *
 * Aucune exception ne remonte : un nom introuvable est rendu vide à l'écran (la ligne reste
 * affichée avec « client non rattaché »), il ne fait jamais échouer la liste.
 * @param {Array<string>} uids - Identifiants de comptes (doublons et vides tolérés)
 * @returns {Promise<Map<string, string>>} uid → nom d'affichage
 */
const resoudreNomsClients = async (uids) => {
  const uniques = [...new Set((uids || []).filter(Boolean))];
  const noms = new Map();
  // Firestore `in` accepte jusqu'à 30 valeurs : on borne par lots de 10 (coût minimal, les envois
  // en échec étant peu nombreux).
  const TAILLE_LOT = 10;
  for (let index = 0; index < uniques.length; index += TAILLE_LOT) {
    const lot = uniques.slice(index, index + TAILLE_LOT);
    try {
      const snapshot = await getDocs(query(
        collection(firestore, USERS_COLLECTION),
        where(documentId(), 'in', lot)
      ));
      snapshot.docs.forEach((docSnap) => {
        const data = docSnap.data() || {};
        const nom = `${data.firstName || ''} ${data.lastName || ''}`.trim();
        noms.set(docSnap.id, nom || data.email || 'Client sans nom');
      });
    } catch (error) {
      console.warn('clientService: noms clients introuvables', error?.message);
    }
  }
  return noms;
};

/** Enrichit une liste d'envois avec le nom du client rattaché (`meta.userId` → `users`). */
const enrichirAvecClients = async (envois) => {
  const noms = await resoudreNomsClients(envois.map((envoi) => envoi.meta.userId));
  return envois.map((envoi) => ({
    ...envoi,
    clientUid: envoi.meta.userId || '',
    clientNom: envoi.meta.userId ? (noms.get(envoi.meta.userId) || '') : ''
  }));
};

/** Lecture transversale bornée : une requête d'égalité par état, `limit` par état, jamais d'`orderBy`. */
const lireEnvoisParEtats = async (etats, max) => {
  const snapshots = await Promise.all(etats.map((etat) => getDocs(query(
    collection(firestore, MAIL_COLLECTION),
    where('delivery.state', '==', etat),
    limit(max)
  ))));
  const envois = snapshots.flatMap((snapshot) => snapshot.docs
    .map((docSnap) => mapMail(docSnap.id, docSnap.data())));
  return trierParDate(envois);
};

/**
 * Envois EN ÉCHEC, toutes fiches confondues — LECTURE SEULE, vue transverse (lot I5).
 *
 * C'est le pendant « parc » de `getClientEmails()` : au lieu de chercher dans la fiche d'un client
 * connu, on part du journal `mail` pour retrouver les envois qui n'ont PAS abouti (motif du
 * demandeur, §1.1 nº 2 : « cet utilisateur dit ne pas avoir reçu son email »).
 *
 * ⚠ Aucun `orderBy` Firestore : trier sur `meta.timestamp` exigerait un index composite à déployer
 * pour un écran de consultation (DI5-3 / DI5-12). Le tri est fait EN MÉMOIRE par le module pur
 * (`utils/mailDelivery.trierParDate`), et la borne `MAILS_LIMIT` (200) borne la lecture.
 * @param {number} [max] - Nombre maximum d'envois lus
 * @returns {Promise<Array<Object>>} Envois en échec, du plus récent au plus ancien, nom du client résolu
 */
export const getFailedEmails = async (max = MAILS_LIMIT) => {
  try {
    return await enrichirAvecClients(await lireEnvoisParEtats(['ERROR'], max));
  } catch (error) {
    console.warn('clientService: envois en échec introuvables', error?.message);
    throw error;
  }
};

/**
 * Envois EN ATTENTE — jamais présentés comme des échecs (DI5-7, bancs B1 et B4).
 *
 * Chargé À LA DEMANDE depuis le sous-onglet « Emails en échec » : la vue par défaut ne montre QUE
 * les échecs ; seul un filtre explicite révèle l'attente (`PENDING` / `PROCESSING`), donc aucun coût
 * de lecture tant que personne ne le demande. Deux requêtes d'égalité ⇒ aucun index.
 * @param {number} [max] - Nombre maximum d'envois lus par état
 * @returns {Promise<Array<Object>>} Envois en attente, du plus récent au plus ancien
 */
export const getPendingEmails = async (max = MAILS_LIMIT) => {
  try {
    return await enrichirAvecClients(await lireEnvoisParEtats(['PENDING', 'PROCESSING'], max));
  } catch (error) {
    console.warn('clientService: envois en attente introuvables', error?.message);
    throw error;
  }
};


